import React, { useState, useEffect, useRef } from 'react';

// Counter animation hook
const useCountUp = (end, duration = 2000, shouldStart = false) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (!shouldStart) return;
    
    let startTime;
    let animationFrame;
    
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(end * easeOutQuart));
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };
    
    animationFrame = requestAnimationFrame(animate);
    
    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [end, duration, shouldStart]);
  
  return count;
};

// Intersection Observer hook to trigger animation when visible
const useIntersectionObserver = (options) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [wasIntersected, setWasIntersected] = useState(false);
  const elementRef = useRef();

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !wasIntersected) {
        setIsIntersecting(true);
        setWasIntersected(true);
      }
    }, options);

    observer.observe(element);
    return () => observer.unobserve(element);
  }, [options, wasIntersected]);

  return [elementRef, isIntersecting];
};

const stats = [
  { name: 'Dashboards modernized into governed experiences', value: '300+', numericValue: 300 },
  { name: 'Active enterprise accounts', value: '20+', numericValue: 20 },
  { name: 'Snowflake and semantic foundations delivered', value: '50+', numericValue: 50 },
  { name: 'Enterprise AI and agentic solutions delivered', value: '20+', numericValue: 20 },
];

function prefersReducedMotion() {
  return typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function StatCard({ stat, delay, active }) {
  const reduceMotion = prefersReducedMotion();
  const count = useCountUp(stat.numericValue, 2000 + delay, active && !reduceMotion);
  const suffix = stat.value.replace(/^\d+/, '');
  const display = reduceMotion || !active ? (reduceMotion ? stat.numericValue : count) : count;

  return (
    <div className="flex flex-col items-start justify-center rounded-2xl bg-white p-6 border border-ink-200 shadow-sm transition-shadow hover:shadow-md">
      <dd className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-ink-900">
        {reduceMotion ? stat.numericValue : display}{suffix}
      </dd>
      <dt className="mt-3 text-sm font-medium text-ink-600 leading-6">{stat.name}</dt>
    </div>
  );
}

export default function WorkWithUs() {
  const [statsRef, isStatsVisible] = useIntersectionObserver({
    threshold: 0.3,
    rootMargin: '0px 0px -100px 0px'
  });

  return (
    <div className="section">
      <div className="container-x min-w-0">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-4 items-start">
          <div className="lg:col-span-3 min-w-0">
            <p className="eyebrow">Platforms we build on</p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-semibold tracking-tight text-ink-900">
              Snowflake, Power BI, Tableau, QuickSight, Streamlit, Postgres, Cortex Search, AWS, and Azure.
            </h2>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-ink-600">
              We partner with enterprise teams to modernize analytics stacks, build semantic layers,
              and launch conversational BI and decision intelligence systems that close the context gap.
            </p>
          </div>

          <div className="lg:text-right">
            <a href="/contact" className="btn-primary btn-lg w-full sm:w-auto lg:inline-flex">
              Talk to our team
            </a>
          </div>
        </div>

        <div className="mx-auto mt-12 max-w-2xl lg:mx-0 lg:max-w-none">
          <dl ref={statsRef} className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {stats.map((stat, index) => (
              <StatCard
                key={stat.name}
                stat={stat}
                delay={index * 200}
                active={isStatsVisible}
              />
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
}
